import { customersApiClient } from './apiClient';
import { recordServerDirection, serverDirectionFor } from '@/utils/serverDirection';
import { isNearAirport } from '@/utils/airports';
import { buildBookingPayloadParts } from '@/utils/bookingPayload';
import { storage } from '@/utils/storage';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { parseRideDate } from '@/utils/dateUtils';

/**
 * The booking flow's pickup time, formatted the way processingBooking formats it for the
 * booking itself — same keys, same local getters. The picker already works in APP_TIMEZONE
 * wall-clock, so converting here would double-shift it. Returns null until both parts are
 * chosen; calculate-cost simply omits the field then.
 */
// The pickup time the CURRENT hopnTripRouteHash was calculated for. Kept next to the hash
// because the two are a pair: the backend buckets the hash by pickup window, so a hash is
// only valid for the time it was requested with.
const ROUTE_SCHEDULED_AT_KEY = 'hopnRouteScheduledAt';

/**
 * The pickup window the cached route_hash was calculated for ('' = live traffic).
 * Callers compare their own pickup time against this to decide whether a re-quote is
 * needed at all — the date/time wheel uses it to stay silent when nothing has moved.
 */
export function routeScheduledAt() {
  return sessionStorage.getItem(ROUTE_SCHEDULED_AT_KEY) || '';
}

/** "Y-m-d H:i:s" from a Date, in the wall clock the whole booking flow works in. */
export function toScheduledString(date) {
  if (!date || Number.isNaN(date.getTime?.())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
}

function storedScheduledAt() {
  const date = storage.get(STORAGE_KEYS.RIDE_DATE);
  const time = storage.get(STORAGE_KEYS.RIDE_TIME);
  if (!date || !time) return null;
  // parseRideDate, not new Date(): RIDE_DATE is a bare "YYYY-MM-DD", which new Date()
  // reads as UTC midnight while the getters below are local — a day early west of UTC.
  const d = parseRideDate(date);
  const t = new Date(time);
  if (!d || Number.isNaN(t.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(t.getHours())}:${pad(t.getMinutes())}:00`;
}

const API_ENDPOINTS = {
  TRIPS: '/trips',
  CALL_TOKEN: '/call-token',
};

const getTripEndpoints = (tripId) => ({
  DETAILS: `/trips/${tripId}`,
  RESTORE: `/trips/${tripId}/restore`,
  UPDATE: `/trips/${tripId}`,
  CONFIRM: `/trips/${tripId}/confirm`,
  CANCEL: `/trips/${tripId}/cancel`,
  RETURN: `/trips/${tripId}/return`,
  REBOOK: `/trips/${tripId}/rebook`,
  RETRY_HOLD: `/trips/${tripId}/retry-hold`,
  ATTACH_PAYMENT_METHOD: `/trips/${tripId}/payment-method`,
  LOCATION: `/trips/${tripId}/location`,
  REACHED_PICKUP: `/trips/${tripId}/reached-pickup`,
  CALL_PROXY: `/trips/${tripId}/call-proxy`,
});

export const tripService = {
  /**
   * THE NUMBER TO DIAL TO REACH THE DRIVER — a masking number, never theirs.
   *
   * ⚠️ THE REAL NUMBER IS NEVER SENT TO THIS APP AT ALL, which is the entire point. The
   * backend allocates a proxy session on the first request and hands back a number that
   * routes to the driver for the life of the trip. Neither side ever learns the other's,
   * and a call placed after the ride simply does not connect.
   *
   * ⚠️ AND IT IS ALLOCATED LAZILY, so this is a real request rather than a lookup. Do
   * not call it on render to have the number ready — that books a proxy session for every
   * passenger who merely looks at a trip. Call it when someone presses call.
   *
   * @param {string|number} tripId
   * @returns {Promise<{success: boolean, data: {call: {proxy_number: string, expires_at: string}}|null}>}
   */
  /**
   * A TWILIO ACCESS TOKEN, for placing the call inside the page.
   *
   * ⚠️ IT EXPIRES, and `expires_in` says when. The SDK does not renew it: a device left
   * registered past the expiry fails to connect with an error that says nothing useful
   * about why, so the token is fetched per call rather than once and kept.
   *
   * ⚠️ AND IT CAN LEGITIMATELY BE UNAVAILABLE. The endpoint answers 503 when it cannot
   * mint one, which is a real state rather than a fault to retry into — the caller falls
   * back to the dialler.
   */
  async getCallToken() {
    return customersApiClient.get(API_ENDPOINTS.CALL_TOKEN);
  },

  async getCallProxy(tripId) {
    if (!tripId) return {success: false, data: null, error: 'A trip is required.'};
    return customersApiClient.get(getTripEndpoints(tripId).CALL_PROXY);
  },

  /**
   * SEND THE PASSENGER'S OWN POSITION so the driver's map can draw a pin on them.
   *
   * The mirror of the driver's `submitLocation`: the backend broadcasts it straight out as
   * `PassengerLocationUpdated` on the trip channel, which is what the driver portal's
   * en-route map already listens for. Nothing is persisted — a passenger's pings are not
   * needed to reconstruct a route — so a dropped one costs nothing and there is no reason
   * to retry it.
   *
   * ⚠️ IT IS A DISCLOSURE, not a read. Every other use of the browser's geolocation in
   * this app points the passenger's own camera at themselves; this hands their position to
   * another person. That is why it is a separate call with its own caller-side gate rather
   * than something `useMyLocation` does on everyone's behalf — see `useShareMyLocation`.
   *
   * @param {string|number} tripId
   * @param {{lat: number, lng: number, accuracy_m?: number, heading_deg?: number, speed_kph?: number}} fix
   */
  async submitLocation(tripId, fix) {
    const lat = Number(fix?.lat);
    const lng = Number(fix?.lng);
    if (!tripId || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return {success: false, data: null, error: 'A latitude and longitude are required.'};
    }

    /* ⚠️ `accuracy_m` IS ALWAYS SENT, and zero when the browser gave none. The driver
       side and dispatch read it to decide how much to trust a position, and an absent
       field there is indistinguishable from a perfect fix. Zero is the same convention the
       mobile apps use. */
    const payload = {lat, lng, accuracy_m: Math.round(Number(fix?.accuracy_m) || 0)};

    /* ⚠️ NEGATIVE MEANS UNKNOWN, AND UNKNOWN IS OMITTED RATHER THAN FORWARDED. Both the
       browser and the mobile platforms use -1 for "no heading" and "no speed"; sending it
       through would put a car pointing at bearing -1 on someone's map. */
    const heading = Number(fix?.heading_deg);
    if (Number.isFinite(heading) && heading >= 0) payload.heading_deg = Math.round(heading);

    /* ⚠️ THE BROWSER GIVES METRES PER SECOND AND THE API TAKES KM/H. Sending the raw
       value is a walker reported at 1.4 km/h and a car at 25 — wrong by a factor of 3.6 in
       a field nothing validates. Two decimal places, matching the mobile payload exactly. */
    const speedMps = Number(fix?.speed_mps);
    if (Number.isFinite(speedMps) && speedMps >= 0) payload.speed_kph = +(speedMps * 3.6).toFixed(2);

    return customersApiClient.post(getTripEndpoints(tripId).LOCATION, payload);
  },

  /**
   * "I AM AT THE PICKUP POINT" — sent once, by the app, the first time the device is inside the
   * admin-set radius (see `useReachedPickupGeofence`). The server stamps the trip, tells the
   * driver, and ignores repeats, so a retry is harmless.
   *
   * Errors the caller cares about: `trip_not_active` (422, stop watching) and
   * `outside_pickup_radius` (422, only when the admin verifies positions server-side — keep
   * watching and fire again on the next in-radius fix).
   *
   * @param {string|number} tripId
   * @param {{lat: number, lng: number, accuracy_m?: number}} fix
   */
  async reportReachedPickup(tripId, fix) {
    const lat = Number(fix?.lat);
    const lng = Number(fix?.lng);
    if (!tripId || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return {success: false, data: null, error: 'A latitude and longitude are required.'};
    }
    const payload = {lat, lng, recorded_at: new Date().toISOString()};
    const accuracy = Math.round(Number(fix?.accuracy_m));
    if (Number.isFinite(accuracy) && accuracy >= 0) payload.accuracy_m = accuracy;
    return customersApiClient.post(getTripEndpoints(tripId).REACHED_PICKUP, payload);
  },

  /**
   * Calculate route between origin/destination (+ optional stopovers).
   * Used to pre-validate that the passenger's selected locations form a reachable route
   * (e.g. catches cross-region mistakes before the trip is submitted).
   * @param {Object} origin - {lat, lng, address, city?, state?, country?}
   * @param {Object} destination - {lat, lng, address, city?, state?, country?}
   * @param {Array} stopOvers - Optional array of {lat, lng, address, ...}
   */
  /**
   * @param {Object} opts
   * @param {string|null} [opts.scheduledPickupAt] "Y-m-d H:i:s" pickup time. With it the
   *   returned duration reflects PREDICTED TRAFFIC for that window instead of "leaving
   *   now", and the route_hash is bucketed by that window — so a different time yields a
   *   different hash and must be re-requested. Omit the option entirely to fall back to
   *   the BOOKING FLOW's stored pickup time; the trip pages must pass their own live
   *   value, since sessionStorage there belongs to a different (booking) session.
   */
  async calculateRoute(origin, destination, stopOvers = [], {scheduledPickupAt} = {}) {
    const pickupAt = scheduledPickupAt !== undefined ? scheduledPickupAt : storedScheduledAt();

    const payload = {
      origin: {
        lat: origin.lat,
        lng: origin.lng,
        address: origin.address,
        city: origin.city || '',
        state: origin.state || '',
        country: origin.country || '',
        postal_code: origin.postal_code || '',
      },
      destination: {
        lat: destination.lat,
        lng: destination.lng,
        address: destination.address,
        city: destination.city || '',
        state: destination.state || '',
        country: destination.country || '',
        postal_code: destination.postal_code || '',
      },
    };

    if (stopOvers.length > 0) {
      payload.stop_overs = stopOvers
        .map((s) => ({
          lat: s.lat,
          lng: s.lng,
          address: s.address,
          city: s.city || '',
          state: s.state || '',
          country: s.country || '',
          postal_code: s.postal_code || '',
        }))
        .filter((s) => s.lat && s.lng && s.address);
    }

    if (pickupAt) payload.scheduled_pickup_at = pickupAt;

    const result = await customersApiClient.post('/trips/calculate-route', payload);

    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to calculate route',
      };
    }

    // Remember WHICH pickup window this hash was priced for. '' = quoted against live
    // traffic (no time chosen yet). recalculateCost compares it against the currently
    // stored pickup time and refreshes the route when they diverge.
    sessionStorage.setItem(ROUTE_SCHEDULED_AT_KEY, pickupAt || '');

    // The server's own From/To Airport decision, when its admin switch is on (null when off).
    recordServerDirection(origin, destination, result.data);

    return {
      success: true,
      data: result.data,
      error: null,
    };
  },

  /**
   * Re-run /calculate-route when the BOOKING FLOW's chosen pickup time no longer matches
   * the window the stored route_hash was calculated for. The hash is bucketed by pickup
   * window, so changing the time invalidates it exactly like changing an address does.
   *
   * No-op when unchanged, when no route exists yet, or when the destinations aren't in
   * storage; a failure is swallowed so the caller falls back to the existing hash rather
   * than blocking the passenger on a routing hiccup.
   *
   * @returns {Promise<boolean>} true when a fresh hash was stored
   */
  async refreshRouteForPickupTime() {
    const routeHash = sessionStorage.getItem('hopnTripRouteHash');
    if (!routeHash) return false;

    const wanted = storedScheduledAt() || '';
    if (wanted === (sessionStorage.getItem(ROUTE_SCHEDULED_AT_KEY) || '')) return false;

    // destinations = [pickup, dropoff, ...stopovers]
    const destinations = JSON.parse(sessionStorage.getItem('secondBannerDestinations') || '[]');
    const origin = destinations[0];
    const destination = destinations[1];
    if (!origin?.lat || !destination?.lat) return false;

    try {
      const result = await this.calculateRoute(origin, destination, destinations.slice(2));
      if (!result.success) return false;
      const hash = result.data?.route_hash ?? result.data?.routeHash;
      if (!hash) return false;
      sessionStorage.setItem('hopnTripRouteHash', hash);
      if (result.data?.route_data) {
        sessionStorage.setItem('hopnTripRouteBreakDown', JSON.stringify(result.data.route_data));
      }
      return true;
    } catch (err) {
      console.warn('[hopn] route refresh for new pickup time failed — keeping the previous route', err);
      return false;
    }
  },

  /**
   * Fetch all trips for authenticated user
   * @param {Object} filters - Optional query params (status, per_page, sort_by, sort_order, etc.)
   */
  async getTrips(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v));
      } else {
        params.append(key, value);
      }
    });
    const query = params.toString();
    const url = query ? `${API_ENDPOINTS.TRIPS}?${query}` : API_ENDPOINTS.TRIPS;

    const result = await customersApiClient.get(url);

    if (!result.success) {
      return {
        success: false,
        data: [],
        pagination: null,
        error: result.error || 'Failed to fetch trips',
      };
    }

    return {
      success: true,
      data: result.data?.trips || [],
      pagination: result.data?.pagination || null,
      error: null,
    };
  },

  /**
   * Fetch details for a specific trip
   * @param {string|number} tripId
   * @param {Object} options
   */
  async getTripDetails(tripId, options = {}) {
    const endpoints = getTripEndpoints(tripId);
    let url = `${endpoints.DETAILS}?include_selectables=true`;
    if (options.withoutPolyline) url += '&without_polyline=true';

    const result = await customersApiClient.get(url);

    if (!result.success) {
      return {
        success: false,
        data: null,
        paymentMethods: [],
        error: result.error || 'Failed to fetch trip details',
      };
    }

    return {
      success: true,
      data: result.data?.trip || null,
      paymentMethods: result.data?.trip?.passenger?.payment_methods || [],
      error: null,
    };
  },

  /**
   * Recover a cancelled trip
   * @param {string|number} tripId
   */
  async recoverTrip(tripId) {
    const endpoints = getTripEndpoints(tripId);
    const result = await customersApiClient.post(endpoints.RESTORE, {});

    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to recover trip',
        raw: result.raw,
      };
    }

    return {
      success: true,
      data: result.data,
      error: null,
    };
  },

  /**
   * Update a trip
   * @param {string|number} tripId
   * @param {Object} payload - Fields to update
   */
  async updateTrip(tripId, payload) {
    const endpoints = getTripEndpoints(tripId);
    const result = await customersApiClient.post(endpoints.UPDATE, payload);

    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to update trip',
        statusCode: result.statusCode,
        raw: result.raw,
      };
    }

    return {
      success: true,
      data: result.data,
      error: null,
    };
  },
  /**
   * Confirm a Draft trip → live booking.
   * Takes the SAME payload as POST /trips (route_hash, vehicle_type_id, scheduled_at and
   * one of payment_method_id/use_wallet_amount are required) and overwrites the Draft's
   * stored values WHOLESALE — an omitted field is cleared, so send everything the
   * passenger can see. Draft status only; see isTripNotDraftError.
   * @param {string|number} tripId
   * @param {Object} payload
   */
  async confirmTrip(tripId, payload) {
    const endpoints = getTripEndpoints(tripId);
    const result = await customersApiClient.post(endpoints.CONFIRM, payload);

    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to confirm trip',
        statusCode: result.statusCode,
        raw: result.raw,
      };
    }

    return {
      success: true,
      data: result.data,
      error: null,
    };
  },

  /**
   * Create a return trip for an existing parent trip.
   * Takes NO body — the backend reads only the trip in the URL and silently ignores
   * anything sent. The Draft it returns inherits route/vehicle/passengers/luggage from the
   * parent but has null scheduled_at, flight_number, payment_method_id and NO PRICE
   * (final_price, subtotal, pricing_breakdown are all null). Price comes from
   * calculate-cost; the booking is finalised by confirmTrip.
   * @param {string|number} parentTripId
   */
  async createReturnTrip(parentTripId) {
    const endpoints = getTripEndpoints(parentTripId);
    const result = await customersApiClient.post(endpoints.RETURN);

    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to create return trip',
        statusCode: result.statusCode,
        raw: result.raw,
      };
    }

    const trip = result.data?.trip || result.data;
    const newTripId = typeof trip === 'object' ? (trip?.id ?? null) : trip;

    return {
      success: true,
      data: { trip, newTripId },
      error: null,
    };
  },

  /**
   * Fetch a paginated trip list from one of the dedicated category endpoints
   * (/trips/upcoming, /trips/completed, /trips/cancelled).
   * @param {string} path - e.g. '/trips/completed'
   * @param {Object} options
   */
  async getTripList(path, { page = 1, perPage = 10 } = {}) {
    const params = new URLSearchParams();
    if (page) params.append('page', page);
    if (perPage) params.append('per_page', perPage);
    const query = params.toString();
    const url = query ? `${path}?${query}` : path;

    const result = await customersApiClient.get(url);

    if (!result.success) {
      return {
        success: false,
        data: [],
        pagination: null,
        error: result.error || 'Failed to fetch trips',
      };
    }

    return {
      success: true,
      data: result.data?.trips || [],
      pagination: result.data?.pagination || null,
      error: null,
    };
  },

  /**
   * Fetch the authenticated user's ongoing trips (EnRoute / InProgress)
   * @param {Object} options
   */
  getOngoingTrips(options = {}) {
    return this.getTripList('/trips/ongoing', options);
  },

  /**
   * Fetch the authenticated user's upcoming trips (soonest first)
   * @param {Object} options
   */
  getUpcomingTrips(options = {}) {
    return this.getTripList('/trips/upcoming', options);
  },

  /**
   * Fetch the authenticated user's completed trips (most recently completed first)
   * @param {Object} options
   */
  getCompletedTrips(options = {}) {
    return this.getTripList('/trips/completed', options);
  },

  /**
   * Fetch the authenticated user's cancelled trips (includes no-shows, newest first)
   * @param {Object} options
   */
  getCancelledTrips(options = {}) {
    return this.getTripList('/trips/cancelled', options);
  },

  /**
   * Rebook a trip — creates a new standalone trip copying details from the source.
   * @param {string|number} tripId
   */
  async rebookTrip(tripId) {
    const endpoints = getTripEndpoints(tripId);
    const result = await customersApiClient.post(endpoints.REBOOK);

    if (!result.success) {
      return {
        success: false,
        data: null,
        newTripId: null,
        error: result.error || 'Failed to rebook trip',
        statusCode: result.statusCode,
        raw: result.raw,
      };
    }

    const trip = result.data?.trip || result.data;
    const newTripId = typeof trip === 'object' ? (trip?.id ?? null) : trip;

    return {
      success: true,
      data: { trip, newTripId },
      newTripId,
      error: null,
    };
  },

  /**
   * Retry placing a hold on the trip's currently attached payment method.
   * No body. Used when status is PendingHold/PaymentHoldFailed.
   * @param {string|number} tripId
   */
  async retryHold(tripId) {
    const endpoints = getTripEndpoints(tripId);
    const result = await customersApiClient.post(endpoints.RETRY_HOLD);
    if (!result.success) {
      return { success: false, error: result.error || 'Failed to retry hold', statusCode: result.statusCode, raw: result.raw };
    }
    return { success: true, data: result.data, error: null };
  },

  /**
   * Attach a payment method to the trip and immediately retry the hold.
   * Body must contain exactly one of `payment_method_id` (saved card) or
   * `square_token` (fresh Square nonce + payment_method_type/display_name/is_default).
   * @param {string|number} tripId
   * @param {{payment_method_id?: number}|{square_token: string, payment_method_type: string, display_name?: string, is_default?: boolean}} payload
   */
  async attachPaymentMethod(tripId, payload) {
    const endpoints = getTripEndpoints(tripId);
    const result = await customersApiClient.post(endpoints.ATTACH_PAYMENT_METHOD, payload);
    if (!result.success) {
      return { success: false, error: result.error || 'Failed to attach payment method', statusCode: result.statusCode, raw: result.raw };
    }
    return { success: true, data: result.data, error: null };
  },

  /**
   * Cancel a trip
   * @param {string|number} tripId
   * @param {string} reason
   */
  async cancelTrip(tripId, reason) {
    const endpoints = getTripEndpoints(tripId);
    const result = await customersApiClient.post(endpoints.CANCEL, { cancellation_reason: reason });

    if (!result.success) {
      console.error('[cancelTrip] failed', {
        tripId,
        reason,
        statusCode: result.statusCode,
        error: result.error,
        raw: result.raw,
      });
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to cancel trip',
        statusCode: result.statusCode,
        raw: result.raw,
      };
    }

    return {
      success: true,
      data: result.data,
      error: null,
    };
  },

  /**
   * Build a fingerprint of all pricing-relevant data from sessionStorage.
   * Used to detect whether a recalculation is actually needed.
   */
  buildPricingFingerprint() {
    const { passengers, childSeats, luggage } = buildBookingPayloadParts();
    const discountCode = sessionStorage.getItem('hopnCouponValue') || '';
    const routeHash = sessionStorage.getItem('hopnTripRouteHash') || '';
    return JSON.stringify({
      passengers: passengers.map((p) => ({ id: p.passenger_type_id, qty: p.quantity })),
      childSeats: childSeats.map((s) => ({ id: s.child_seat_type_id, qty: s.quantity })),
      luggage: luggage.map((l) => ({ id: l.luggage_type_id, qty: l.quantity })),
      discountCode,
      routeHash,
      // The pickup time is part of the quote now: it decides which traffic window the
      // route duration is priced against, and the route_hash is bucketed by it. Without
      // it here a time-only change would leave the cached car list (and its stale drive
      // time) in place.
      scheduledAt: storedScheduledAt() || '',
    });
  },

  needsRecalculation() {
    const cached = sessionStorage.getItem('hopnAllCarsApiData');
    if (!cached) return true;
    const currentFingerprint = this.buildPricingFingerprint();
    const storedFingerprint = sessionStorage.getItem('hopnPricingFingerprint');
    return currentFingerprint !== storedFingerprint;
  },

  /**
   * Calculate vehicle costs for a route.
   *
   * `tripId` — send it whenever the quote is for an EXISTING draft (return / rebook).
   * The online-discount percentage is configured per trip type and this endpoint assumes
   * *Initial* when it can't tell, while /confirm prices by the draft's own type — which is
   * how a return booking quoted 20% and confirmed at 25%. With trip_id the backend reads
   * the type (and the direction, unless trip_direction is sent) off the draft itself, so
   * the quote is computed exactly the way the booking will be. Must belong to the
   * authenticated passenger — someone else's id returns 404 trip_not_found.
   *
   * `scheduledAt` — the pickup time ('Y-m-d H:i:s'). Surge is time-windowed, so a quote
   * without it can't see the window the booking will land in and comes back at base price.
   * Accepted as an alias for scheduled_pickup_at.
   */
  async calculateCost(routeHash, passengers, childSeats, luggage, discountCode = null, driverGratuityPercentage = null, tripDirection = null, {tripId = null, scheduledAt = null} = {}) {
    const payload = {
      route_hash: routeHash,
      passenger_types: passengers.filter((p) => p.quantity > 0),
      child_seats: childSeats.filter((s) => s.quantity > 0),
      luggage: luggage.filter((l) => l.quantity > 0),
    };
    if (tripId) payload.trip_id = tripId;
    if (scheduledAt) payload.scheduled_at = scheduledAt;
    if (discountCode) payload.discount_code = discountCode;
    // Only send a gratuity we actually HAVE — a real, non-zero percentage. Everything else
    // ('' / undefined / "undefined" / null / 0) omits the key so the backend applies its own
    // default. `!= null` used to let the empty cases through as parseInt → NaN, and 0 is not a
    // chosen tip either: it comes back on a priced vehicle that had no gratuity, and echoing it
    // would pin every later quote to an untipped fare.
    const gratuity = parseInt(driverGratuityPercentage);
    if (Number.isFinite(gratuity) && gratuity > 0) payload.gratuity_percentage = gratuity;
    if (tripDirection) payload.trip_direction = tripDirection;

    const result = await customersApiClient.post('/trips/calculate-cost', payload);

    if (!result.success) {
      return { success: false, data: null, error: result.error || 'Unable to calculate vehicle prices', raw: result.raw };
    }

    const vehicles = result.data?.vehicle_types;
    if (!vehicles?.length) {
      return { success: false, data: null, error: 'No vehicles available for selected trip' };
    }

    return { success: true, data: vehicles, error: null };
  },

  /**
   * Calculate the cancellation & modification policies for a trip.
   * Stateless preview that powers the policy popups. Accepts the trip params
   * explicitly so callers can source them from sessionStorage (booking flow)
   * or a loaded trip. Returns the full data payload ({cancellation, modification,
   * details, policy_popup}) so future content can read whichever part it needs.
   */
  async calculatePolicies({ trip_direction, distance_meters } = {}) {
    // Only trip_direction + distance_meters — the endpoint returns the config-based
    // policy bands, NOT a per-trip price. (scheduled_at / final_price were never part
    // of this request and are intentionally omitted.)
    const result = await customersApiClient.post('/trips/calculate-policies', {
      trip_direction,
      distance_meters,
    });

    if (!result.success) {
      return { success: false, data: null, error: result.error || 'Unable to load policies', raw: result.raw };
    }

    return { success: true, data: result.data, error: null };
  },

  /**
   * Recalculate cost using stored route and passenger data.
   * Used when entering carSelection / orderDetails edit-flow.
   */
  async recalculateCost(driverGratuityPercentage = null) {
    // The stored route was calculated for a pickup window. If the passenger has since
    // moved the date/time (the wheel step, or "Use Recommended Pickup"), that hash is for
    // the wrong window — the duration is wrong AND the backend expects the hash matching
    // the time. Refresh here so every repricing path gets it without each one remembering.
    await this.refreshRouteForPickupTime();

    const routeHash = sessionStorage.getItem('hopnTripRouteHash');
    if (!routeHash) {
      return { success: false, data: null, error: 'No route data found. Please recalculate route.' };
    }

    const { passengers, childSeats, luggage } = buildBookingPayloadParts();

    const discountCode = sessionStorage.getItem('hopnCouponValue') || null;

    const destinations = JSON.parse(sessionStorage.getItem('secondBannerDestinations') || '[]');
    const firstDest = destinations[0];
    const lastDest = destinations[destinations.length - 1];

    const isPickupAirport = firstDest?.lat && firstDest?.lng ? !!isNearAirport(firstDest.lat, firstDest.lng) : false;
    const isDropOffAirport = lastDest?.lat && lastDest?.lng ? !!isNearAirport(lastDest.lat, lastDest.lng) : false;
    // The server's direction (switch on) wins; otherwise our own coordinate match.
    const tripDirection = serverDirectionFor(destinations[0], destinations[1])?.direction
      ?? (isPickupAirport ? 'from_airport' : isDropOffAirport ? 'to_airport' : 'others');

    const scheduledAt = storedScheduledAt();

    let result = await this.calculateCost(routeHash, passengers, childSeats, luggage, discountCode, driverGratuityPercentage, tripDirection, {scheduledAt});

    if (isRouteHashError(result) && destinations.length >= 2) {
      const routeResult = await this.calculateRoute(destinations[0], destinations[1], destinations.slice(2));
      if (routeResult.success) {
        const newHash = routeResult.data.route_hash ?? routeResult.data.routeHash;
        const newRouteData = routeResult.data.route_data ?? routeResult.data.routeData;
        sessionStorage.setItem('hopnTripRouteHash', newHash);
        sessionStorage.setItem('hopnTripRouteBreakDown', JSON.stringify(newRouteData));
        sessionStorage.setItem('hopn-mapUi', JSON.stringify(newRouteData));
        result = await this.calculateCost(newHash, passengers, childSeats, luggage, discountCode, driverGratuityPercentage, tripDirection, {scheduledAt});
      }
    }

    if (result.success && result.data) {
      sessionStorage.setItem('hopnAllCarsApiData', JSON.stringify(result.data));
      sessionStorage.setItem('hopnPricingFingerprint', this.buildPricingFingerprint());
    }

    return result;
  },

  /**
   * Create a new trip (book it). Used by processingBooking after the user finalizes.
   */
  async createTrip(payload) {
    const result = await customersApiClient.post('/trips', payload);
    if (!result.success) {
      return {
        success: false,
        data: null,
        error: result.error || 'Failed to create trip',
        statusCode: result.statusCode,
        raw: result.raw,
      };
    }
    return { success: true, data: result.data, error: null };
  },
};

export function isRouteHashError(result) {
  if (!result || result.success) return false;
  const raw = result.raw || {};
  // `route_hash_not_found` = the hash expired (POST /trips/{id}/confirm); the others are
  // the create-time variants. All three mean "re-run calculate-route and retry".
  // `route_hash_expired`: the admin's toll / traffic switches are part of route_hash, so flipping
  // one invalidates outstanding hashes — same remedy, re-run calculate-route.
  return raw.key === 'route_hash_calculation_error'
    || raw.key === 'route_hash_expired'
    || raw.key === 'route_hash_not_found'
    || !!raw.errors?.route_hash;
}

// A trip can only be confirmed while it is a Draft. Once it isn't, the booking already
// went through — do NOT retry, and don't surface it as a failure.
export function isTripNotDraftError(result) {
  return !result?.success && result?.raw?.key === 'trip_not_draft';
}

/**
 * The credit/wallet system is switched off. Booking with use_wallet_amount > 0 now fails
 * with 422 wallet_disabled instead of silently ignoring the credits. Recoverable: drop the
 * wallet amount and let the passenger pay by card.
 */
export function isWalletDisabledError(result) {
  return !result?.success && result?.raw?.key === 'wallet_disabled';
}

/**
 * The saved card can't be charged for this trip.
 *
 * As of Aug 2026 every place a card is attached — booking, trip edit, hold-retry attach —
 * verifies it up front: owned by this passenger, active, unexpired, and tokenized in the
 * Square environment the trip charges through. Cards saved before the test/live split
 * carry no environment tag, so a live passenger could book with a sandbox-era card and
 * have the hold fail by cron minutes later. It is refused at submit instead.
 *
 * On the attach endpoint this key REPLACED the old `404 payment_method_not_found` for a
 * card belonging to someone else — that case is now a 422 under this key.
 *
 * Recoverable: the passenger picks another card or adds one. `raw.message` is written for
 * the passenger (expired cards get their own wording under the same key), so show it.
 */
export function isPaymentMethodUnusableError(result) {
  return !result?.success && result?.raw?.key === 'payment_method_unusable';
}

/** Fallback copy when the API sends the key without a message. */
export const PAYMENT_METHOD_UNUSABLE_MESSAGE =
  "This saved card can't be used for this booking. Please choose another card or add a new one.";

// Friendly copy for the /confirm-specific error keys; anything else falls back to the
// server's own message.
const CONFIRM_ERROR_COPY = {
  trip_not_draft: 'This booking has already been confirmed.',
  flight_number_must_differ: "Please enter the flight number for this leg — it can't be the same as the original booking's.",
  return_before_original: "The return pickup has to be after the original trip's pickup time.",
};

export function confirmErrorMessage(result) {
  const key = result?.raw?.key;
  if (CONFIRM_ERROR_COPY[key]) return CONFIRM_ERROR_COPY[key];
  // Field validation (errors.scheduled_at, errors.route_hash, errors.vehicle_type_id, …)
  const firstField = result?.raw?.errors && Object.values(result.raw.errors)[0];
  const firstMsg = Array.isArray(firstField) ? firstField[0] : firstField;
  return firstMsg || result?.error || 'Failed to confirm trip';
}

export default tripService;
